/**
 * 주간 보고 보기 전용 목업.
 * 기준이 되는 마지막 주는 2026-10-23, 10월 4주차다.
 * 이름과 단계명은 수행 보고의 가명이다.
 */
import type { WeeklyMilestone, WeeklyMovingWeek, WeeklyPhase, WeeklyReportArchive, WeeklySnapshot, WeeklyWorkStatus, WeeklyWorkstream } from "./weekly-report-model";

const STREAMS = [
  { id: "master", name: "기준정보", ownerId: "master" },
  { id: "procure", name: "구매·자재", ownerId: "procure" },
  { id: "prod", name: "생산", ownerId: "prod" },
  { id: "sales", name: "영업·출하", ownerId: "sales" },
  { id: "close", name: "결산 연계", ownerId: "close" },
  { id: "test", name: "통합 테스트", ownerId: "qa" },
  { id: "data", name: "데이터 전환", ownerId: "data" },
] as const;

type WorkCell = [plan: number, actual: number, status: WeeklyWorkStatus, thisWeek: string, nextWeek: string];

function work(cells: WorkCell[]): WeeklyWorkstream[] {
  return STREAMS.map((stream, index) => {
    const cell = cells[index] ?? [0, 0, "notStarted", "", ""];
    const [plan, actual, status, thisWeek, nextWeek] = cell;
    return { ...stream, plan, actual, status, thisWeek, nextWeek };
  });
}

function phases(build: number, test: number, cutover: number, design = 100): WeeklyPhase[] {
  return [
    { id: "kickoff", label: "착수", progress: 100 },
    { id: "analysis", label: "분석", progress: 100 },
    { id: "design", label: "설계", progress: design },
    { id: "build", label: "구축", progress: build },
    { id: "test", label: "테스트", progress: test },
    { id: "cutover", label: "이행", progress: cutover },
    { id: "open", label: "오픈", progress: 0 },
  ];
}

function milestones(openForecast: string, masterActual?: string, trainForecast = "2026-11-06", testForecast = "2026-10-30"): WeeklyMilestone[] {
  const masterLate = masterActual != null && masterActual > "2026-10-09";
  return [
    {
      id: "master",
      name: "기준정보 확정",
      ownerId: "master",
      planned: "2026-10-09",
      actual: masterActual,
      forecast: masterActual ? undefined : "2026-10-09",
      rag: masterLate ? "watch" : "onTrack",
    },
    {
      id: "weekly",
      name: "주간 공정 회의",
      ownerId: "pm",
      planned: "2026-10-28",
      forecast: "2026-10-28",
      rag: "onTrack",
    },
    {
      id: "train",
      name: "사용자 교육",
      ownerId: "pmo",
      planned: "2026-11-06",
      forecast: trainForecast,
      rag: "onTrack",
    },
    {
      id: "test",
      name: "통합 테스트 착수 보고",
      ownerId: "qa",
      planned: "2026-10-30",
      forecast: testForecast,
      rag: testForecast > "2026-10-30" ? "watch" : "onTrack",
    },
    {
      id: "open",
      name: "오픈",
      ownerId: "pm",
      planned: "2026-11-27",
      forecast: openForecast,
      finish: true,
      rag: openForecast > "2026-11-27" ? "watch" : "onTrack",
      note: openForecast > "2026-11-27" ? "결산 범위가 남으면 더 밀립니다." : undefined,
    },
    {
      id: "stable",
      name: "안정화 종료",
      ownerId: "pm",
      planned: "2026-12-11",
      forecast: "2026-12-11",
      rag: "onTrack",
    },
  ];
}

function moving(current: string, delayed?: string): { label: string; weeks: WeeklyMovingWeek[] } {
  const starts = ["2026-09-21", "2026-09-28", "2026-10-05", "2026-10-12", "2026-10-19"];
  return {
    label: "구축",
    weeks: starts.map((start) => ({
      start,
      current: start === current,
      delayed: start === delayed,
    })),
  };
}

const author = { authorId: "pm", approverIds: ["client", "qa"] };

export const WEEKLY_REPORT: WeeklyReportArchive = {
  projectName: "제조사 ERP 구축",
  clientName: "한빛제조",
  period: { start: "2026-07-06", end: "2026-12-11", span: "약 5개월" },
  people: [
    { id: "client", name: "강하늘", role: "고객 총괄" },
    { id: "pm", name: "윤도윤", role: "수행 PM" },
    { id: "pmo", name: "서민재", role: "PMO" },
    { id: "design", name: "정하린", role: "설계" },
    { id: "master", name: "남지호", role: "기준정보" },
    { id: "procure", name: "배수아", role: "구매" },
    { id: "prod", name: "한도윤", role: "생산" },
    { id: "sales", name: "구하준", role: "영업" },
    { id: "close", name: "표이안", role: "결산" },
    { id: "qa", name: "임서연", role: "품질" },
    { id: "data", name: "노은우", role: "데이터" },
  ],
  issues: [
    { id: "terms", title: "화면 용어 불일치", severity: "low", ownerId: "design", opened: "2026-09-08", due: "2026-09-18", closed: "2026-09-16" },
    { id: "role", title: "권한 롤 누락", severity: "medium", ownerId: "pmo", opened: "2026-09-15", due: "2026-09-25", closed: "2026-09-29" },
    {
      id: "map",
      title: "레거시 발주 코드 매핑",
      severity: "high",
      ownerId: "master",
      opened: "2026-09-18",
      due: "2026-10-02",
      escalateFrom: "2026-10-16",
      note: "구 코드와 신 코드가 겹칩니다.",
    },
    { id: "iface", title: "인터페이스 샘플 지연", severity: "medium", ownerId: "prod", opened: "2026-09-28", due: "2026-10-09", closed: "2026-10-13" },
    { id: "seed", title: "테스트 데이터 부족", severity: "medium", ownerId: "qa", opened: "2026-10-06", due: "2026-10-16", closed: "2026-10-17" },
    {
      id: "account",
      title: "결산 테스트 계정",
      severity: "medium",
      ownerId: "close",
      opened: "2026-10-14",
      due: "2026-10-21",
      closed: "2026-10-22",
      note: "고객사에서 계정을 발급했습니다.",
    },
    { id: "dup", title: "생산 실적 코드 중복", severity: "low", ownerId: "prod", opened: "2026-10-16", due: "2026-10-30" },
    { id: "warehouse", title: "현장 창고 실사 일정", severity: "medium", ownerId: "procure", opened: "2026-10-20", due: "2026-10-28", escalateFrom: "2026-10-20" },
  ],
  decisions: [
    { id: "train", title: "사용자 교육 대상 명단", ownerId: "client", asked: "2026-09-28", neededBy: "2026-10-09", decided: "2026-10-08" },
    { id: "scope", title: "결산 연계 범위 확정", ownerId: "client", asked: "2026-10-02", neededBy: "2026-10-16" },
    { id: "warehouse", title: "현장 창고 실사 일정", ownerId: "client", asked: "2026-10-20", neededBy: "2026-10-28", attachments: ["창고 실사 요청.pdf"] },
  ],
  weeks: [
    week({
      id: "w1",
      index: 1,
      label: "9월 3주차",
      period: { start: "2026-09-14", end: "2026-09-20" },
      asOf: "2026-09-18",
      rag: "onTrack",
      ragReason: "설계가 끝났고, 구축 실적이 계획을 조금 앞섭니다.",
      messages: [
        { id: "w1-a", tone: "fact", text: "설계 산출물이 잠겼고, 구축은 기준정보와 구매부터 시작했습니다." },
        { id: "w1-b", tone: "fact", text: "실적 공정 48%는 계획 46%보다 2%p 앞입니다." },
        { id: "w1-c", tone: "watch", text: "발주 코드 매핑이 오늘 열렸습니다. 기한은 10월 2일입니다." },
      ],
      measures: { planProgress: 46, actualProgress: 48 },
      milestones: milestones("2026-11-27"),
      phases: phases(20, 0, 0),
      workstreams: work([
        [40, 45, "onTrack", "코드 체계를 맞췄습니다.", "코드 값을 채웁니다."],
        [15, 18, "onTrack", "발주 흐름을 그렸습니다.", "화면 초안을 만듭니다."],
        [8, 10, "onTrack", "실적 항목을 모았습니다.", "인터페이스 초안을 적습니다."],
        [0, 0, "notStarted", "", "출하 범위를 적습니다."],
        [0, 0, "notStarted", "", ""],
        [0, 0, "notStarted", "", ""],
        [5, 5, "onTrack", "원천 목록을 적었습니다.", "매핑 칸을 만듭니다."],
      ]),
      agenda: [
        { id: "w1-design", date: "2026-09-18", title: "설계 종료 점검", ownerId: "design", shift: "done" },
        { id: "w1-weekly", date: "2026-09-23", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
      ],
    }),
    week({
      id: "w2",
      index: 2,
      label: "9월 4주차",
      period: { start: "2026-09-21", end: "2026-09-27" },
      asOf: "2026-09-25",
      rag: "onTrack",
      ragReason: "구축이 계획대로 움직이고, 오픈 날은 그대로입니다.",
      messages: [
        { id: "w2-a", tone: "fact", text: "기준정보와 구매 화면이 계획 근처에서 진행 중입니다." },
        { id: "w2-b", tone: "fact", text: "권한 롤은 아직 열려 있고, 기한은 9월 25일입니다." },
      ],
      measures: { planProgress: 54, actualProgress: 55 },
      milestones: milestones("2026-11-27"),
      phases: phases(32, 0, 0),
      moving: moving("2026-09-21"),
      workstreams: work([
        [55, 58, "onTrack", "코드 값을 채웠습니다.", "이관 리허설을 준비합니다."],
        [28, 30, "onTrack", "발주 화면 초안을 올렸습니다.", "입고 화면을 잇습니다."],
        [16, 18, "onTrack", "인터페이스 초안을 적었습니다.", "샘플 데이터를 받습니다."],
        [5, 4, "watch", "출하 범위를 적기 시작했습니다.", "흐름을 확정합니다."],
        [0, 0, "notStarted", "", ""],
        [0, 0, "notStarted", "", ""],
        [10, 12, "onTrack", "매핑 칸을 만들었습니다.", "원천 열을 맞춥니다."],
      ]),
      agenda: [
        { id: "w2-weekly", date: "2026-09-30", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
        { id: "w2-master", date: "2026-10-09", title: "기준정보 확정", ownerId: "master", shift: "held" },
      ],
    }),
    week({
      id: "w3",
      index: 3,
      label: "10월 1주차",
      period: { start: "2026-09-28", end: "2026-10-04" },
      asOf: "2026-10-02",
      rag: "watch",
      ragReason: "인터페이스 샘플이 늦어 실적이 계획을 1%p 밑돕니다.",
      messages: [
        { id: "w3-a", tone: "watch", text: "생산 인터페이스 샘플이 기한 10월 9일을 앞두고 아직 없습니다." },
        { id: "w3-b", tone: "ask", text: "사용자 교육 대상 명단을 10월 9일까지 받아 두어야 합니다." },
        { id: "w3-c", tone: "ask", text: "결산 연계 범위를 10월 16일까지 정해 주셔야 합니다." },
      ],
      measures: { planProgress: 62, actualProgress: 61 },
      milestones: milestones("2026-11-27"),
      phases: phases(40, 5, 0),
      moving: moving("2026-09-28"),
      workstreams: work([
        [70, 68, "watch", "이관 리허설을 한 차례 했습니다.", "코드 차이를 줄입니다."],
        [42, 40, "watch", "입고 화면을 이었으나 예외가 남았습니다.", "예외 칸을 막습니다."],
        [28, 30, "onTrack", "샘플을 기다리는 동안 매핑을 적었습니다.", "샘플로 1차를 올립니다."],
        [12, 10, "watch", "출하 흐름 초안이 하루 늦습니다.", "화면 목록을 잠급니다."],
        [5, 4, "onTrack", "연계 대상을 적기 시작했습니다.", "범위를 회의에 올립니다."],
        [4, 5, "onTrack", "시나리오 목차를 잡았습니다.", "구매 케이스를 적습니다."],
        [16, 14, "watch", "원천 열 일부가 비어 있습니다.", "빈 열을 채웁니다."],
      ]),
      agenda: [
        { id: "w3-weekly", date: "2026-10-07", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
        { id: "w3-master", date: "2026-10-09", title: "기준정보 확정", ownerId: "master", shift: "held" },
        { id: "w3-scope", date: "2026-10-16", title: "결산 범위 결정", ownerId: "client", shift: "held" },
      ],
    }),
    week({
      id: "w4",
      index: 4,
      label: "10월 2주차",
      period: { start: "2026-10-05", end: "2026-10-11" },
      asOf: "2026-10-09",
      rag: "watch",
      ragReason: "기준정보 확정이 기한을 넘겼고, 발주 코드는 여전히 열려 있습니다.",
      messages: [
        { id: "w4-a", tone: "watch", text: "기준정보 확정은 10월 9일에 끝나지 못했습니다." },
        { id: "w4-b", tone: "watch", text: "발주 코드 매핑은 기한 10월 2일을 지났습니다." },
        { id: "w4-c", tone: "fact", text: "교육 대상 명단은 10월 8일에 결정되었습니다." },
      ],
      measures: { planProgress: 68, actualProgress: 64 },
      milestones: milestones("2026-11-27"),
      phases: phases(48, 8, 4),
      moving: moving("2026-10-05"),
      workstreams: work([
        [85, 78, "watch", "코드 차이를 줄였으나 확정은 못 했습니다.", "남은 코드를 잠급니다."],
        [58, 52, "watch", "입고 예외가 남아 발주 연동이 늦습니다.", "예외를 닫습니다."],
        [40, 42, "onTrack", "샘플로 실적 1차를 시작했습니다.", "값을 맞춥니다."],
        [22, 16, "watch", "출하 화면 목록이 계획보다 짧습니다.", "빠진 화면을 넣습니다."],
        [12, 8, "watch", "결산 범위 회의가 결정 없이 끝났습니다.", "범위를 다시 올립니다."],
        [8, 8, "onTrack", "구매 케이스를 적었습니다.", "생산 케이스를 적습니다."],
        [20, 18, "watch", "빈 열 일부가 남았습니다.", "열을 채웁니다."],
      ]),
      agenda: [
        { id: "w4-master", date: "2026-10-16", title: "기준정보 확정", ownerId: "master", shift: "later" },
        { id: "w4-weekly", date: "2026-10-14", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
        { id: "w4-scope", date: "2026-10-16", title: "결산 범위 결정", ownerId: "client", shift: "held" },
      ],
    }),
    week({
      id: "w5",
      index: 5,
      label: "10월 3주차",
      period: { start: "2026-10-12", end: "2026-10-18" },
      asOf: "2026-10-16",
      rag: "watch",
      ragReason: "오픈이 4일 밀렸고, 미결 이슈는 4건입니다.",
      messages: [
        { id: "w5-a", tone: "watch", text: "오픈 전망이 11월 27일에서 12월 1일로 옮겨, 계획보다 4일입니다." },
        { id: "w5-b", tone: "risk", text: "발주 코드 매핑은 오늘로 28일이고, 상신이 필요합니다." },
        { id: "w5-c", tone: "ask", text: "결산 연계 범위는 오늘이 필요일이었으나 결정되지 않았습니다." },
        { id: "w5-d", tone: "fact", text: "기준정보는 10월 16일에 확정되었습니다. 계획은 10월 9일이었습니다." },
      ],
      measures: { planProgress: 74, actualProgress: 68 },
      milestones: milestones("2026-12-01", "2026-10-16"),
      phases: phases(55, 14, 8),
      moving: moving("2026-10-12", "2026-10-12"),
      workstreams: work([
        [100, 96, "watch", "기준정보를 확정했으나 이관 확인이 남았습니다.", "이관을 마칩니다."],
        [72, 66, "watch", "발주·입고 연동을 이었으나 코드가 막습니다.", "매핑이 열리는 대로 검증합니다."],
        [50, 52, "onTrack", "실적 값을 1차로 맞췄습니다.", "중복 코드를 가립니다."],
        [35, 24, "watch", "출하 화면이 계획보다 적습니다.", "출하 착수를 넓힙니다."],
        [22, 14, "watch", "범위 문안을 다시 올렸으나 결정이 없습니다.", "결정 전 개발을 멈춥니다."],
        [12, 12, "onTrack", "생산 케이스를 적었습니다.", "시나리오 초안을 묶습니다."],
        [26, 22, "watch", "매핑 초안의 빈칸이 남았습니다.", "빈칸을 채웁니다."],
      ]),
      agenda: [
        { id: "w5-master", date: "2026-10-16", title: "기준정보 확정", ownerId: "master", shift: "done" },
        { id: "w5-weekly", date: "2026-10-21", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
        { id: "w5-test", date: "2026-10-30", title: "통합 테스트 착수 보고", ownerId: "qa", shift: "held" },
      ],
    }),
    week({
      id: "w6",
      index: 6,
      label: "10월 4주차",
      period: { start: "2026-10-19", end: "2026-10-25" },
      asOf: "2026-10-23",
      rag: "watch",
      ragReason: "일정은 6일 밀렸고, 결산 범위와 발주 코드가 남아 있습니다.",
      messages: [
        { id: "w6-a", tone: "risk", text: "오픈 전망이 11월 27일에서 12월 3일로 밀려, 계획보다 6일입니다." },
        { id: "w6-b", tone: "risk", text: "결산 연계 범위가 10월 16일에 필요했던 결정 없이 멈춰 있습니다." },
        { id: "w6-c", tone: "watch", text: "발주 코드 매핑은 9월 18일에 열렸고, 오늘로 35일입니다." },
        { id: "w6-d", tone: "fact", text: "결산 테스트 계정은 10월 22일에 닫혀, 미결은 4건에서 3건이 되었습니다." },
        { id: "w6-e", tone: "ask", text: "창고 실사 일정은 10월 28일 공정 회의 전에 정해 주셔야 합니다." },
      ],
      measures: { planProgress: 78, actualProgress: 71 },
      milestones: milestones("2026-12-03", "2026-10-16", "2026-10-30", "2026-11-06"),
      phases: phases(62, 20, 10),
      moving: moving("2026-10-19", "2026-10-12"),
      workstreams: work([
        [100, 100, "done", "코드 이관 리허설을 마쳤습니다.", ""],
        [82, 75, "watch", "발주·입고 화면을 이었으나 코드 매핑이 남습니다.", "입고 예외를 처리합니다."],
        [58, 60, "onTrack", "생산 실적 인터페이스 1차를 올렸습니다.", "실적 값을 검증합니다."],
        [48, 35, "watch", "출하 화면 착수를 시작했습니다.", "출하 흐름을 잇습니다."],
        [30, 20, "blocked", "결산 범위 합의가 멈췄습니다.", "범위를 확정합니다."],
        [10, 15, "onTrack", "통합 시나리오 초안을 썼습니다.", "테스트 환경을 구성합니다."],
        [30, 25, "watch", "전환 매핑 초안을 맞췄습니다.", "매핑 빈칸을 채웁니다."],
      ]),
      agenda: [
        { id: "agenda-base", date: "2026-10-16", title: "기준정보 확정", ownerId: "master", shift: "done" },
        { id: "agenda-weekly", date: "2026-10-28", title: "주간 공정 회의", ownerId: "pm", shift: "held" },
        { id: "agenda-train", date: "2026-10-30", title: "사용자 교육", ownerId: "pmo", shift: "sooner" },
        { id: "agenda-test", date: "2026-11-06", title: "통합 테스트 착수 보고", ownerId: "qa", shift: "later" },
      ],
    }),
  ],
};

function week(snapshot: Omit<WeeklySnapshot, "authorId" | "approverIds">): WeeklySnapshot {
  return { ...author, ...snapshot };
}
